import {LoaderFunctionArgs} from 'react-router-dom';
import {getRootStore} from '../../app.bootstrap';
import {getAppTracer} from '../../../app.tracer';
import {RouteName} from '../../router/route-names';
import {getDashboardLanguage} from '../helper/general.helper';
import {getSubDomainFromUrl} from '@techsee/techsee-common/lib/utils/url-utils';
import {PlatformType} from '@techsee/techsee-common/lib/constants/utils.constant';
import {AccountAssetsType} from '@techsee/techsee-client-infra/lib/services/AccountBrandingService';
import {IInvitationEndedStore, InvitationEndedStore} from './invitation-ended.stores';
import {InvitationEndedController, InvitationEndedSessionTypes, InvitationEndedReason} from './controller';

const trace = getAppTracer('invitation-ended.loader');

export async function invitationEndedRouteLoader(args: LoaderFunctionArgs): Promise<IInvitationEndedStore> {
    trace.info('Starting');
    const rootStore = getRootStore();
    const url = new URL(args.request.url);
    const params = url.searchParams;

    const err = params.get('err');
    const sessionType = params.get('sessionType') || InvitationEndedSessionTypes.regular;
    const reason = params.get('reason');

    let currentUser: any;
    try {
        currentUser = await rootStore.userService.getCurrentUser();
    } catch (err) {
        trace.error('Error while getting the current user', {error: err});
    }

    let accountData: any = {};
    if (currentUser?.accountId) {
        try {
            accountData = await rootStore.accountService.getAccount(currentUser.accountId);
        } catch (err) {
            trace.error('Error while getting the current account', {error: err});
        }
    }

    const localizationService = rootStore.localizationService;
    const localizationInitialized = localizationService.isInitialized();
    const brandingService = rootStore.accountBrandingService;
    let brandingData: AccountAssetsType[] = [];
    const promises: Promise<any>[] = [];

    if (!localizationInitialized && accountData?._id) {
        const accountLanguage = getDashboardLanguage(accountData);
        localizationService.setAccountData(accountData._id, accountLanguage);
        promises.push(localizationService.init());
    }

    if (accountData?._id) {
        promises.push(
            brandingService
                .getAccountBrandingAssets(accountData._id, PlatformType.dashboard)
                .then((assets) => {
                    brandingData = assets;
                })
                .catch(async (err) => {
                    await getRootStore().tsReactStateHelper.navigateTo(RouteName.LOGIN);
                    trace.error('Branding assets fetch failed', {error: err?.message});
                })
        );
    } else {
        const subDomain = getSubDomainFromUrl(window.location.href);
        promises.push(
            brandingService
                .getAccountBrandingBySubDomain(subDomain, PlatformType.dashboard)
                .then((assets) => {
                    brandingData = assets;
                })
                .catch(async (err) => {
                    await getRootStore().tsReactStateHelper.navigateTo(RouteName.LOGIN);
                    trace.error('Branding by subdomain fetch failed', {error: err?.message});
                })
        );
    }

    const settled = await Promise.allSettled(promises);

    for (const result of settled) {
        if (result.status === 'rejected' && result.reason instanceof Response) {
            throw result.reason;
        }
    }

    const goToInvitePage =
        sessionType === InvitationEndedSessionTypes.warmTransfer
            ? () => {
                  void rootStore.tsReactStateHelper.navigateTo(RouteName.INVITE);
              }
            : () => {};

    const translate = localizationService.translate.bind(localizationService);
    const sessionState = {
        ended: true,
        isExpired: !!err,
        sessionType: sessionType as InvitationEndedSessionTypes,
        reason: reason as InvitationEndedReason
    };

    const controller = new InvitationEndedController(
        translate,
        rootStore.domUtilsService,
        sessionState,
        goToInvitePage,
        brandingData
    );

    const store = new InvitationEndedStore(controller);

    trace.info('Completed successfully');
    return store;
}
